import { articles, contentByPath, contentItems } from "../content";
import { guideEnhancementFor } from "../content/guide-enhancements";
import type { ContentItem } from "../content/types";

export const SITE_URL = "https://aquariumhelpline.com";
export const SITE_NAME = "Aquarium Helpline";

export type SeoData = {
  title: string;
  description: string;
  canonicalPath: string;
  image?: string;
  type: "article" | "website";
  publishedAt?: string;
  modifiedAt?: string;
  noindex?: boolean;
};

const staticSeo = new Map<string, SeoData>([
  [
    "/",
    {
      title: "Aquarium Helpline | Expert Aquarium Answers, Fast",
      description:
        "Get practical aquarium troubleshooting help, clear care guides, and a 15-minute video consultation with an experienced aquarium professional.",
      canonicalPath: "/",
      type: "website",
    },
  ],
  [
    "/articles/",
    {
      title: "Aquarium Care Guides | Aquarium Helpline",
      description:
        "Straightforward guides for aquarium water, fish health, filtration, nitrogen cycling, aquascaping, turtles, and paludariums.",
      canonicalPath: "/articles/",
      type: "website",
    },
  ],
  [
    "/consultation/",
    {
      title: "Request an Aquarium Consultation | Aquarium Helpline",
      description:
        "Tell us what is happening in your aquarium and request a focused 15-minute video consultation.",
      canonicalPath: "/consultation/",
      type: "website",
    },
  ],
  [
    "/privacy/",
    {
      title: "Privacy | Aquarium Helpline",
      description: "How Aquarium Helpline handles contact and consultation request information.",
      canonicalPath: "/privacy/",
      type: "website",
    },
  ],
]);

export function normalizePath(pathname: string): string {
  const withoutQuery = pathname.split(/[?#]/, 1)[0] || "/";
  const withLeadingSlash = withoutQuery.startsWith("/") ? withoutQuery : `/${withoutQuery}`;
  if (withLeadingSlash === "/") return "/";
  return `${withLeadingSlash.replace(/\/+$/, "")}/`;
}

function seoForContent(item: ContentItem): SeoData {
  const enhancement = item.type === "post" ? guideEnhancementFor(item) : undefined;
  return {
    title: `${enhancement?.metaTitle ?? item.title} | ${SITE_NAME}`,
    description: enhancement?.metaDescription ?? item.summary,
    canonicalPath: item.canonicalPath,
    image: item.featuredImage ?? undefined,
    type: item.type === "post" ? "article" : "website",
    publishedAt: item.publishedAt,
    modifiedAt: item.modifiedAt,
  };
}

export function getSeoData(pathname: string): SeoData {
  const path = normalizePath(pathname);
  const item = contentByPath(path);
  if (item) return seoForContent(item);
  const page = staticSeo.get(path);
  if (page) return page;
  return {
    title: `Page not found | ${SITE_NAME}`,
    description: "That aquarium help page could not be found. Browse our practical care guides instead.",
    canonicalPath: path,
    type: "website",
    noindex: true,
  };
}

export const prerenderPaths = [
  "/",
  "/articles/",
  "/consultation/",
  "/privacy/",
  ...contentItems.map((item) => item.canonicalPath),
];

export function siteStructuredData(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${SITE_URL}/#organization`,
        name: SITE_NAME,
        url: SITE_URL,
        email: "support@aquariumhelpline.com",
        sameAs: ["https://aquatic-contractors.com"],
      },
      {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        name: SITE_NAME,
        url: SITE_URL,
        publisher: { "@id": `${SITE_URL}/#organization` },
      },
    ],
  };
}

export function articleStructuredData(item: ContentItem): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: item.title,
    description: item.summary,
    datePublished: item.publishedAt,
    dateModified: item.modifiedAt,
    mainEntityOfPage: `${SITE_URL}${item.canonicalPath}`,
    image: item.featuredImage ? `${SITE_URL}${item.featuredImage}` : undefined,
    author: { "@id": `${SITE_URL}/#organization`, "@type": "Organization", name: SITE_NAME },
    publisher: { "@id": `${SITE_URL}/#organization` },
    inLanguage: "en-US",
  };
}

export function breadcrumbStructuredData(item: ContentItem): Record<string, unknown> {
  const parent = item.type === "post"
    ? [{
        "@type": "ListItem",
        position: 2,
        name: "Aquarium care guides",
        item: `${SITE_URL}/articles/`,
      }]
    : [];
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      ...parent,
      {
        "@type": "ListItem",
        position: parent.length + 2,
        name: item.title,
        item: `${SITE_URL}${item.canonicalPath}`,
      },
    ],
  };
}

export function faqStructuredData(item: ContentItem): Record<string, unknown> {
  const { faqs } = guideEnhancementFor(item);
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: { "@type": "Answer", text: faq.answer },
    })),
  };
}

export function consultationStructuredData(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "15-minute aquarium video consultation",
    description:
      "A focused video consultation to review an aquarium, its water readings, recent changes, and practical next steps.",
    provider: { "@id": `${SITE_URL}/#organization` },
    url: `${SITE_URL}/consultation/`,
    serviceType: "Aquarium troubleshooting consultation",
  };
}

export function latestArticleDate(items = articles): string {
  return items[0]?.modifiedAt ?? new Date(0).toISOString();
}
